import { useLocalStorage } from '@mantine/hooks'
import { useAtomValue } from 'jotai'
import { noop } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import type { ValueOf } from 'type-fest'
import type { SupportedLocaleCodes } from 'src/i18n/constants'
import { SUPPORTED_LOCALES } from 'src/i18n/constants'
import { Currency } from 'src/services/apollo/generated/graphql'
import { useIPCountry } from 'src/services/helpers/ip'
import { getLocale, localeAtom } from 'src/stores/locale'

export interface CurrencyDetails {
  code: SupportedCurrencies
  symbol: string
  valueMultiplier: number

  // preferNarrowRepresentation: the amount in this currency typically takes more
  // space than other currencies, so we should prefer a narrow representation
  preferNarrowRepresentation?: boolean
}

type SupportedCurrencies = ValueOf<typeof Currency>

/**
 * @deprecated Better to use `currencyData` instead
 */
export const currencies: CurrencyDetails[] = [
  {
    code: Currency.Usd,
    symbol: '$',
    valueMultiplier: 100,
  },
  // {
  //   code: Currency.Eur,
  //   symbol: '€',
  //   valueMultiplier: 100,
  // },
  {
    code: Currency.Jpy,
    symbol: '¥',
    valueMultiplier: 1,
    preferNarrowRepresentation: true,
  },
]
export const currencyData = Object.fromEntries(
  currencies.map(it => [it.code, it]),
) as Record<Currency, CurrencyDetails>

export interface FormatCurrencyOptions {
  currency: Currency
  locale?: SupportedLocaleCodes
}

/**
 * Format a price in a formal way
 */
export function formatCurrency(
  value: number,
  opts: FormatCurrencyOptions,
): string {
  return new Intl.NumberFormat(opts.locale ?? getLocale(), {
    style: 'currency',
    currency: opts.currency,
    currencyDisplay: 'code',
  }).format(value)
}

/**
 * @deprecated Prefer `formatCurrency`
 */
export function formatPrice(currency?: Currency, value?: number): string {
  if (!currency || value == null) return ''
  return formatCurrency(value, { currency })
}

/**
 * Hook to get a currency format function
 */
export function useFormatCurrency(opts?: Partial<FormatCurrencyOptions>) {
  const locale = useAtomValue(localeAtom)
  const [currency] = useCurrency()

  return useCallback(
    (value: number, _opts?: Partial<FormatCurrencyOptions>) =>
      formatCurrency(value, {
        currency: opts?.currency ?? currency,
        locale: opts?.locale ?? locale,
        ..._opts,
      }),
    [opts?.currency, opts?.locale, currency, locale],
  )
}

export const useCurrency = () => {
  const { data, isLoading } = useIPCountry()
  const [debugCurrency] = useLocalStorage<string>({
    key: 'debug::currency-override',
    defaultValue: '',
  })

  const resolved = !!debugCurrency || !isLoading

  if (debugCurrency) {
    return [debugCurrency as Currency, noop, resolved] as const
  }

  // Especially for Japan, we want to use JPY
  if (data === 'JP') {
    return [Currency.Jpy, noop, resolved] as const
  }

  return [Currency.Usd, noop, resolved] as const
}

export const useCurrencyDetails = (currency: string) => {
  return useMemo(
    () => currencies.find(currencyDetails => currencyDetails.code === currency),
    [currency],
  )
}

export const guessUserCurrency = (): Currency | undefined => {
  const locale = getLocale()
  return SUPPORTED_LOCALES[locale].currency
}
